/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { isApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import dayjs from "dayjs";
import { useLocation } from "react-use";
import {
  EstateV3ExportMutationVariables,
  useEstateV3ExportMutation,
} from "~/generated/graphql";
import { SUCCESS_TEXT, dateFormat } from "~/constants";
import { useQueryState } from "~/hooks";
import { showSimpleInfo, showSuccess } from "~/services/toast";
import { handleError, toInputError } from "~/tools";

type ExportQueryState = {
  periodStart?: string;
};

type Filters = EstateV3ExportMutationVariables["input"]["filters"];

export function useEstateRecordsExport(callback: () => void) {
  const [estateExport, mutationState] = useEstateV3ExportMutation();
  const { user } = useAuth0();
  const { href } = useLocation();

  const {
    state: { periodStart },
  } = useQueryState<ExportQueryState>();

  return [
    async (filters: Filters) => {
      try {
        await estateExport({
          variables: {
            input: {
              filters,
              viewUrl: href?.replace(/page=\d+&/, "page=0&") ?? "",
              notificationEmail: user?.email ?? "",
            },
          },
          context: {
            headers: {
              "x-billing-period": dayjs(periodStart).format(
                dateFormat.yearMonth
              ),
            },
          },
        });
        showSuccess(SUCCESS_TEXT.export, false);
        callback();
      } catch (err) {
        const error = toInputError(err);
        const isExportInProgress =
          isApolloError(error) &&
          error.graphQLErrors.some(
            (graphQlError) =>
              graphQlError.extensions.code === "EXPORT_IN_PROGRESS"
          );

        if (isExportInProgress) {
          showSimpleInfo(
            "Similar export action recently requested. Please retry later.",
            false
          );
        } else {
          handleError(error);
        }
        callback();
      }
    },
    mutationState,
  ] as const;
}
